// System tab — tool health dashboard. Read-only, no step-up required.

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { Stat } from "@/components/ui/Stat";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import {
  useAdminSystemTools,
  useRefreshSystemTools,
} from "@/hooks/useAdmin";

const CATEGORY_ORDER = [
  "recon",
  "discovery",
  "fingerprinting",
  "crawling",
  "vuln_scanning",
  "deep_validation",
];

export function AdminSystemTab() {
  const td = useDict().app.adminSystem;
  const { data, isLoading, isError, error } = useAdminSystemTools();
  const refresh = useRefreshSystemTools();

  async function handleRefresh() {
    try {
      const r = await refresh.mutateAsync();
      toast.success(fmt(td.refreshed, { n: r.total }));
    } catch (e) {
      toast.error(`${td.refreshFailed} ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  if (isError) {
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[11px]">
          {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  if (isLoading || !data) return <Loading text={td.checking} />;

  const byCategory: Record<string, typeof data.tools> = {};
  for (const cat of CATEGORY_ORDER) byCategory[cat] = [];
  for (const t of data.tools) {
    if (!byCategory[t.category]) byCategory[t.category] = [];
    byCategory[t.category].push(t);
  }

  const healthPct = Math.round((data.installed / Math.max(data.total, 1)) * 100);
  const healthColor =
    healthPct === 100 ? "rgb(var(--color-success))" : healthPct >= 80 ? "rgb(var(--color-warning))" : "rgb(var(--color-danger))";

  return (
    <div>
      {/* Summary header — stacks on mobile (overall health on its own
          row, 3 stats 3-up below, refresh button full-width). md+ keeps
          the dense single-row layout. */}
      <div className="flex flex-col md:flex-row md:items-stretch gap-[10px] mb-3">
        <Card className="md:flex-[2]">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-[6px]">
            {td.overallHealth}
          </div>
          <div className="flex items-baseline gap-[10px]">
            <div
              className="font-display text-[32px] font-bold"
              style={{ color: healthColor }}
            >
              {healthPct}%
            </div>
            <div className="font-mono text-[11px] text-muted">
              {fmt(td.toolsInstalled, { installed: data.installed, total: data.total })}
            </div>
          </div>
          <div className="mt-[6px] h-[6px] bg-bg rounded-[3px] overflow-hidden">
            <div
              className="h-full transition-[width] duration-300"
              style={{ width: `${healthPct}%`, background: healthColor }}
            />
          </div>
        </Card>
        <div className="grid grid-cols-3 gap-[10px] md:contents">
          <Card className="md:flex-1">
            <Stat label={td.installed} value={data.installed} color="rgb(var(--color-success))" />
          </Card>
          <Card className="md:flex-1">
            <Stat
              label={td.missing}
              value={data.missing}
              color={data.missing > 0 ? "rgb(var(--color-danger))" : "rgb(var(--color-muted))"}
            />
          </Card>
          <Card className="md:flex-1">
            <Stat
              label={td.categories}
              value={Object.keys(td.categoryLabels).length}
              color="rgb(var(--color-accent))"
            />
          </Card>
        </div>
        <div className="flex flex-col justify-center gap-[6px]">
          <Button
            variant="primary"
            onClick={handleRefresh}
            disabled={refresh.isPending}
          >
            {refresh.isPending ? td.checkingBtn : td.refreshBtn}
          </Button>
        </div>
      </div>

      <div className="font-mono text-[9px] text-dim mb-3 text-right">
        {td.lastCheck} {new Date(data.checked_at).toLocaleString()} · {td.cachedFor}{" "}
        {data.cache_ttl_seconds}s
      </div>

      {/* Per-category tables */}
      {CATEGORY_ORDER.filter((cat) => byCategory[cat]?.length > 0).map((cat) => {
        const stats = data.by_category[cat] ?? {
          total: 0,
          installed: 0,
          missing: 0,
        };
        return (
          <div key={cat} className="mb-4">
            <div className="flex items-center gap-[10px] mb-2">
              <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] font-bold">
                ▸ {td.categoryLabels[cat as keyof typeof td.categoryLabels] ?? cat}
              </div>
              <div className="font-mono text-[9px] text-muted">
                {stats.installed}/{stats.total}
              </div>
              {stats.missing > 0 && (
                <div
                  className="px-[6px] py-[1px] rounded-[2px] text-[8px] font-bold uppercase"
                  style={{
                    background: "rgb(var(--color-danger) / 0.133)",
                    border: "1px solid rgb(var(--color-danger) / 0.4)",
                    color: "rgb(var(--color-danger))",
                  }}
                >
                  {stats.missing} {td.missingBadge}
                </div>
              )}
            </div>

            {/* Tool list per category — desktop grid table from md+,
                stacked tool cards below (the 5-col table with a 28px
                indicator + truncated path overflows phone viewports). */}

            {/* Mobile: stacked cards */}
            <div className="md:hidden flex flex-col gap-2">
              {byCategory[cat].map((t) => (
                <Card key={t.name} className="!p-3">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="text-[14px]"
                      style={{ color: t.installed ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}
                    >
                      {t.installed ? "●" : "○"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-[12px] text-content font-semibold">
                        {t.name}
                      </span>
                      <span className="font-mono text-[10px] text-dim ml-2">
                        {t.binary}
                      </span>
                    </div>
                    <span
                      className="font-mono text-[10px]"
                      style={{ color: t.version ? "rgb(var(--color-content))" : "rgb(var(--color-dim))" }}
                    >
                      {t.installed ? (t.version ?? td.unknown) : "—"}
                    </span>
                  </div>
                  {(t.error || t.notes) && (
                    <div className="font-mono text-[10px] mt-1 break-words">
                      {t.error && !t.installed ? (
                        <span className="text-danger">{t.error}</span>
                      ) : t.error ? (
                        <span className="text-warning">⚠ {t.error}</span>
                      ) : (
                        <span className="text-muted">{t.notes}</span>
                      )}
                    </div>
                  )}
                  {t.path && (
                    <div className="font-mono text-[9px] text-dim mt-1 break-all">
                      {t.path}
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {/* Desktop: grid table */}
            <Card className="!p-0 overflow-hidden hidden md:block">
              <div
                className="grid gap-[10px] px-[14px] py-2 bg-bg2 border-b border-stroke font-mono text-[9px] text-muted uppercase tracking-[0.05em] font-semibold items-center"
                style={{
                  gridTemplateColumns: "28px 130px 100px 1.2fr 1fr",
                }}
              >
                <div />
                <div>{td.colTool}</div>
                <div>{td.colVersion}</div>
                <div>{td.colDescError}</div>
                <div>{td.colPath}</div>
              </div>
              {byCategory[cat].map((t) => (
                <div
                  key={t.name}
                  className="grid gap-[10px] px-[14px] py-[10px] border-b border-stroke font-mono text-[11px] items-center"
                  style={{
                    gridTemplateColumns: "28px 130px 100px 1.2fr 1fr",
                  }}
                >
                  <div
                    className="text-center text-[12px]"
                    style={{ color: t.installed ? "rgb(var(--color-success))" : "rgb(var(--color-danger))" }}
                  >
                    {t.installed ? "●" : "○"}
                  </div>
                  <div>
                    <div className="text-content font-semibold">{t.name}</div>
                    <div className="text-dim text-[9px]">{t.binary}</div>
                  </div>
                  <div
                    className="text-[10px]"
                    style={{ color: t.version ? "rgb(var(--color-content))" : "rgb(var(--color-dim))" }}
                  >
                    {t.installed ? (t.version ?? td.unknown) : "—"}
                  </div>
                  <div className="text-[10px] min-w-0 overflow-hidden">
                    {t.error && !t.installed ? (
                      <span className="text-danger">{t.error}</span>
                    ) : t.error ? (
                      <span className="text-warning">⚠ {t.error}</span>
                    ) : (
                      <span className="text-muted truncate block">
                        {t.notes ?? ""}
                      </span>
                    )}
                  </div>
                  <div className="text-[9px] text-dim truncate">
                    {t.path ?? "—"}
                  </div>
                </div>
              ))}
            </Card>
          </div>
        );
      })}

      <div
        className="px-[14px] py-3 rounded mt-4 font-mono text-[10px] text-muted leading-[1.6]"
        style={{
          background: "rgb(var(--color-bg2))",
          border: "1px dashed rgb(var(--color-stroke))",
        }}
      >
        💡 <b>{td.aboutTitle}</b> {td.aboutPre}
        <code className="text-accent">shutil.which()</code>{td.aboutPost}
      </div>
    </div>
  );
}
